import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { TextField } from '@dsny/dsny-component-library'
import { thunks as AuthThunks } from 'reducers/Auth'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { useForm } from 'react-hook-form'
import LoginCard from './LoginCard'
import AuthLayout from '../AuthLayout'
import AuthRolloutBoundary from '../LoginWithRollout'
import ResetPasswordSuccess from './ResetPasswordSuccess'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import { FooterLink } from 'containers/Auth/Auth.styles'
import PasswordValidation from './PasswordValidation'
import { yupResolver } from '@hookform/resolvers/yup'
import { ResetPasswordSchema } from '../authSchema'
import { ContentWrapper } from './SuccessPage/SuccessPage.styles'
import jwt_decode from 'jwt-decode'
import {
  Container,
  FormWrapper,
  FullWidthButton,
  Title
} from './AuthForm.styles'

export default function ResetPassword({
  responsiveBreakpointsPage = RESPONSIVE_BREAKPOINT_PAGES.RESET_PASSWORD,
  responsiveBreakpointsRollout = false
}) {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [email, setEmail] = useState()
  const params = useParams()
  const [displayTips, setDisplayTips] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isExpiredLink, setIsExpiredLink] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isResetPasswordEmailSent, setResetPasswordEmailSent] = useState(false)
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const [isPasswordRequestCalled, setIsPasswordRequestCalled] = useState(false)
  const errorStatus = useSelector((state) => state.authentication.errorMessage)
  const [isWelcomeEmail, setIsWelcomeEmail] = useState(false)
  const token = params?.token

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(ResetPasswordSchema)
  })

  const watchPassword = watch('password', '')

  useEffect(() => {
    if (token) {
      const currentUser = jwt_decode(token)
      setEmail(currentUser.email)
      setIsWelcomeEmail(currentUser?.welcome_email)
    }
  }, [])

  useEffect(() => {
    if (
      errorStatus === 'token expired' ||
      (errorStatus === '' && isPasswordRequestCalled)
    ) {
      setIsExpiredLink(true)
    } else {
      setIsExpiredLink(false)
    }
  }, [errorStatus, isPasswordRequestCalled])

  useEffect(() => {
    if (watchPassword) {
      setDisplayTips(true)
    }
  }, [watchPassword])

  const onSubmit = async (value) => {
    try {
      await dispatch(
        AuthThunks.resetPassword({ password: value.password, token })
      )
      setResetPasswordEmailSent(true)
    } catch (error) {
      setResetPasswordEmailSent(true)
    }
  }

  const OnPasswordRequest = async (value) => {
    try {
      await dispatch(
        AuthThunks.requestForgotPwd({
          email: value.email
        })
      )
      setIsExpiredLink(true)
      setIsPasswordRequestCalled(true)
    } catch (error) {
      console.error(error?.data)
    }
  }

  const handlePasswordValidation = (isValid) => {
    setIsPasswordValid(isValid)
    if (isValid) {
      setDisplayTips(false)
    }
  }

  const content = (
    <AuthLayout>
      <LoginCard>
        {isResetPasswordEmailSent ? (
          <ResetPasswordSuccess
            dataCy="reset-password-success"
            isExpiredLink={isExpiredLink}
            OnPasswordRequest={() => OnPasswordRequest({ email })}
            resetLayout={true}
          />
        ) : (
          <Container>
            <FormWrapper>
              <>
                {isWelcomeEmail ? (
                  <Title>{t('login.set-password')}</Title>
                ) : (
                  <Title>{t('login.reset-password')}</Title>
                )}

                <form
                  data-cy="reset-password-form"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div data-cy="reset-password-email">
                    <TextField
                      id="email"
                      name="email"
                      setting="player"
                      value={email}
                      readOnly={true}
                      wrapperStyles={{ paddingRight: 0, marginBottom: 24 }}
                      labelText={t('signup.email')}
                    />
                  </div>

                  <div data-cy="reset-password-new-password">
                    <TextField
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      {...register('password')}
                      required
                      setting="player"
                      ref={null}
                      highlightColor={theme.colors.interact70}
                      inputRef={register('password').ref}
                      onFocus={() => setDisplayTips(true)}
                      onClick={() => {
                        setShowPassword((prev) => !prev)
                      }}
                      wrapperStyles={{
                        paddingRight: 0,
                        marginBottom: displayTips && !isPasswordValid ? 8 : 24
                      }}
                      labelText={t('signup.new-password')}
                      error={errors?.password?.message}
                      inputStyle={{
                        borderColor: errors?.password && theme.colors.error70
                      }}
                      borderColor={theme.colors.neutralW50}
                      errorStyle={{ color: theme.colors.error70 }}
                      tailingIcon={showPassword ? 'Visibility' : 'VisibilityOff'}
                      isTailingClickable
                      tailingStyle={{
                        paddingTop: '12px',
                        paddingRight: '10px',
                        fill: theme.colors.neutralW70
                      }}
                    />
                  </div>

                  <PasswordValidation
                    displayTips={displayTips}
                    watchPassword={watchPassword}
                    onValidation={handlePasswordValidation}
                  />

                  <div data-cy="reset-password-confirm-password">
                    <TextField
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      {...register('confirmPassword')}
                      required
                      setting="player"
                      ref={null}
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      inputRef={register('confirmPassword').ref}
                      wrapperStyles={{ paddingRight: 0, marginBottom: 32 }}
                      labelText={t('signup.confirm-password')}
                      error={errors?.confirmPassword?.message}
                      inputStyle={{
                        borderColor:
                          errors?.confirmPassword && theme.colors.error70
                      }}
                      borderColor={theme.colors.neutralW50}
                      highlightColor={theme.colors.interact70}
                      errorStyle={{ color: theme.colors.error70 }}
                      tailingIcon={
                        showConfirmPassword ? 'Visibility' : 'VisibilityOff'
                      }
                      isTailingClickable
                      tailingStyle={{
                        paddingTop: '12px',
                        paddingRight: '10px',
                        color: theme.colors.neutralW70
                      }}
                    />
                  </div>
                  <FullWidthButton
                    data-cy="reset-password-submit"
                    type="submit"
                    color="primary"
                    setting="player"
                    disabled={!isPasswordValid}
                  >
                    <div style={{ width: '2rem' }} />
                    {isWelcomeEmail
                      ? t('login.set-password')
                      : t('login.reset-password')}
                  </FullWidthButton>
                </form>
                <FooterLink
                  style={{ textAlign: 'center' }}
                  onClick={() => {
                    navigate('/')
                  }}
                >
                  <ContentWrapper>
                    {t('login.return-to')}
                    {` `}
                    <span
                      style={{
                        fontWeight: 700,
                        color: theme.colors.interact70
                      }}
                    >
                      {t('login.sign-in')}
                    </span>
                    {t('login.page')}
                  </ContentWrapper>
                </FooterLink>
              </>
            </FormWrapper>
          </Container>
        )}
      </LoginCard>
    </AuthLayout>
  )

  return responsiveBreakpointsRollout ? (
    <AuthRolloutBoundary page={responsiveBreakpointsPage}>
      {content}
    </AuthRolloutBoundary>
  ) : (
    content
  )
}

ResetPassword.propTypes = {
  responsiveBreakpointsPage: PropTypes.string,
  responsiveBreakpointsRollout: PropTypes.bool
}
